import { ApiProvider, AnalysisResult, VisualMatch, DecodoMatch, DecodoAnalysisResult } from '../types';
import { supabase } from './supabaseClient';

export class DecodoService {
  
  async analyzeImage(imageFile: File): Promise<DecodoAnalysisResult> {
    try {
      console.log('Starting Decodo image analysis...');
      
      // First upload the image to Supabase storage
      const imageUrl = await this.uploadImageToSupabase(imageFile);
      console.log('Image uploaded to:', imageUrl);
      
      // Call the Supabase Edge Function for Decodo analysis
      const { data, error } = await supabase.functions.invoke('web-analyze-decodo', {
        body: { imageUrl },
      });

      console.log('Decodo analysis response:', { data, error });

      if (error) {
        console.error('Decodo Supabase function error:', error);
        throw new Error(`Failed to send a request to the Edge Function: ${error.message}`);
      }

      if (!data || !data.success) {
        console.error('Decodo function returned unsuccessful response:', data);
        throw new Error(data?.message || 'Unknown error occurred during analysis');
      }

      // Debug what we received from the backend
      console.log('Backend returned result structure:', data.result);
      console.log('Backend result keys:', Object.keys(data.result || {}));

      return this.formatEnhancedDecodoResult(data.result, imageFile);
    } catch (error) {
      console.error('Decodo analysis error:', error);
      throw error;
    }
  }

  // Keep original method for backwards compatibility with search results
  async searchProducts(query: string): Promise<AnalysisResult> {
    try {
      // Call the Supabase Edge Function for Decodo search
      const { data, error } = await supabase.functions.invoke('web-analyze-decodo', {
        body: { query },
      });

      if (error) {
        console.error('Decodo search function error:', error);
        throw new Error(`Failed to send a request to the Edge Function: ${error.message}`);
      }

      if (!data || !data.success) {
        console.error('Decodo search function returned unsuccessful response:', data);
        throw new Error(data?.message || 'Unknown error occurred during search');
      }

      return this.formatSearchResult(data.result, query);
    } catch (error) {
      console.error('Decodo search error:', error);
      throw error;
    }
  }

  private async uploadImageToSupabase(file: File): Promise<string> {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('Please log in to upload an image.');
    }

    const fileExtension = file.name.split('.').pop() || 'jpg';
    const fileName = `${user.id}/decodo-${Date.now()}.${fileExtension}`;

    const { data, error } = await supabase.storage
      .from('images')
      .upload(fileName, file, {
        contentType: file.type,
      });

    if (error) {
      console.error('Supabase Upload Error:', error);
      throw new Error('Failed to upload image to Supabase Storage.');
    }

    if (!data) {
      throw new Error('Upload succeeded but no data was returned.');
    }

    const { data: { publicUrl } } = supabase.storage
      .from('images')
      .getPublicUrl(fileName);

    if (!publicUrl) {
      throw new Error('Failed to get public URL for the uploaded image.');
    }

    return publicUrl;
  }

  private formatSearchResult(data: any, query: string): AnalysisResult {
    const visualMatches: VisualMatch[] = [];
    
    // Add shopping results
    if (data.shopping_results) {
      data.shopping_results.forEach((item: any, index: number) => {
        visualMatches.push({
          title: item.title,
          link: item.link,
          source: item.source,
          price: item.price,
          thumbnail: item.thumbnail,
          position: index + 1
        });
      });
    }

    // Add organic results
    if (data.organic_results) {
      data.organic_results.forEach((item: any, index: number) => {
        visualMatches.push({
          title: item.title,
          link: item.link,
          source: new URL(item.link).hostname,
          thumbnail: item.thumbnail,
          position: visualMatches.length + index + 1
        });
      });
    }

    const aiExplanation = this.generateSearchExplanation(data, visualMatches, query);

    return {
      id: Math.random().toString(36).substring(2, 9) + Date.now().toString(36),
      title: `Search Results for "${query}"`,
      description: aiExplanation,
      value: this.calculateEstimatedValue(visualMatches),
      aiExplanation,
      visualMatches,
      apiProvider: ApiProvider.DECODO,
      timestamp: Date.now()
    };
  }

  private generateAIExplanation(data: any, visualMatches: VisualMatch[]): string {
    const visualCount = data.visual_matches?.length || 0;
    const exactCount = data.exact_matches?.length || 0;
    const similarImagesCount = data.similar_images?.length || 0;
    const pagesWithImagesCount = data.pages_with_matching_images?.length || 0;
    const shoppingCount = data.shopping_results?.length || 0;
    const imageCount = data.images_results?.length || 0;
    const organicCount = data.organic_results?.length || 0;
    const textCount = data.text_results?.length || 0;
    
    let explanation = `Analysis powered by Decodo found ${visualMatches.length} total matches. `;
    
    if (visualCount > 0) {
      explanation += `${visualCount} visual matches, `;
    }
    
    if (exactCount > 0) {
      explanation += `${exactCount} exact matches, `;
    }
    
    if (similarImagesCount > 0) {
      explanation += `${similarImagesCount} similar images, `;
    }
    
    if (pagesWithImagesCount > 0) {
      explanation += `${pagesWithImagesCount} pages with matching images, `;
    }
    
    if (shoppingCount > 0) {
      explanation += `${shoppingCount} shopping results with pricing information, `;
    }
    
    if (imageCount > 0) {
      explanation += `${imageCount} related images, `;
    }
    
    if (organicCount > 0) {
      explanation += `${organicCount} related web pages, `;
    }
    
    if (textCount > 0) {
      explanation += `${textCount} text results. `;
    }

    if (data.knowledge_graph) {
      explanation += `Product information available from knowledge graph. `;
    }

    if (data.related_searches && data.related_searches.length > 0) {
      explanation += `Related searches include: ${data.related_searches.slice(0, 3).join(', ')}.`;
    }

    return explanation;
  }

  private generateSearchExplanation(data: any, visualMatches: VisualMatch[], query: string): string {
    const shoppingCount = data.shopping_results?.length || 0;
    const organicCount = data.organic_results?.length || 0;
    
    let explanation = `Search for "${query}" returned ${visualMatches.length} results via Decodo. `;
    
    if (shoppingCount > 0) {
      explanation += `Found ${shoppingCount} shopping results with pricing. `;
    }
    
    if (organicCount > 0) {
      explanation += `${organicCount} general web results. `;
    }

    if (data.related_searches && data.related_searches.length > 0) {
      explanation += `Related searches: ${data.related_searches.slice(0, 3).join(', ')}.`;
    }

    return explanation;
  }

  private calculateEstimatedValue(visualMatches: VisualMatch[]): string {
    const pricesWithValues = visualMatches
      .filter(match => match.price)
      .map(match => {
        const price = match.price!;
        const numericPrice = parseFloat(price.replace(/[^\d.]/g, ''));
        return isNaN(numericPrice) ? 0 : numericPrice;
      })
      .filter(price => price > 0);

    if (pricesWithValues.length === 0) {
      return 'Price information not available';
    }

    const avgPrice = pricesWithValues.reduce((sum, price) => sum + price, 0) / pricesWithValues.length;
    const minPrice = Math.min(...pricesWithValues);
    const maxPrice = Math.max(...pricesWithValues);

    if (minPrice === maxPrice) {
      return `~$${avgPrice.toFixed(2)}`;
    }

    return `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)} (avg: $${avgPrice.toFixed(2)})`;
  }

  // Test connection to Decodo API via Supabase Edge Function
  async testConnection(): Promise<boolean> {
    try {
      console.log('Testing Decodo connection...');
      
      const { data, error } = await supabase.functions.invoke('web-analyze-decodo', {
        body: { test: true },
      });

      console.log('Decodo test response:', { data, error });

      if (error) {
        console.error('Supabase function error:', error);
        return false;
      }

      return data?.success === true;
    } catch (error) {
      console.error('Decodo connection test failed:', error);
      return false;
    }
  }

  private formatEnhancedDecodoResult(data: any, imageFile: File): DecodoAnalysisResult {
    // Debug the actual data structure
    console.log('DecodoService formatEnhancedDecodoResult received data:', data);
    console.log('Available data keys:', Object.keys(data));
    console.log('Shopping results:', data.shopping_results);
    console.log('Visual matches:', data.visual_matches);
    console.log('Similar images:', data.similar_images);
    console.log('Organic results:', data.organic_results);
    
    const categorizedMatches = {
      shopping: [] as DecodoMatch[],
      visual: [] as DecodoMatch[],
      similar: [] as DecodoMatch[],
      organic: [] as DecodoMatch[]
    };
    
    // Helper function to fix relative URLs
    const fixUrl = (url: string, baseUrl: string = 'https://www.google.com'): string => {
      if (!url) return '#';
      if (url.startsWith('http')) return url;
      if (url.startsWith('/')) return baseUrl + url;
      return baseUrl + '/' + url;
    };

    // Process shopping results
    if (data.shopping_results) {
      data.shopping_results.forEach((item: any, index: number) => {
        const match: DecodoMatch = {
          title: item.title,
          link: fixUrl(item.url || item.link),
          source: item.domain || item.source || (item.url ? new URL(fixUrl(item.url)).hostname : 'Unknown'),
          price: item.price,
          thumbnail: item.url_thumbnail || item.thumbnail || item.image,
          position: index + 1,
          category: 'shopping',
          extracted_price: this.extractPriceValue(item.price),
          currency: 'USD',
          rating: item.rating,
          reviews: item.reviews,
          availability: item.availability || item.stock_information,
          condition: item.condition
        };
        categorizedMatches.shopping.push(match);
      });
    }

    // Process visual matches (exact matches)
    if (data.visual_matches || data.exact_matches) {
      const visualItems = [...(data.visual_matches || []), ...(data.exact_matches || [])];
      visualItems.forEach((item: any, index: number) => {
        const match: DecodoMatch = {
          title: item.title || 'Visual Match',
          link: fixUrl(item.link || item.url || '#'),
          source: item.source || (item.link ? new URL(fixUrl(item.link)).hostname : 'Unknown'),
          price: item.price,
          thumbnail: item.thumbnail || item.image,
          position: index + 1,
          category: 'visual',
          extracted_price: this.extractPriceValue(item.price)
        };
        categorizedMatches.visual.push(match);
      });
    }

    // Process similar images
    if (data.similar_images || data.pages_with_matching_images) {
      const similarItems = [...(data.similar_images || []), ...(data.pages_with_matching_images || [])];
      similarItems.forEach((item: any, index: number) => {
        const match: DecodoMatch = {
          title: item.title || 'Similar Image',
          link: fixUrl(item.link || item.url || '#'),
          source: item.source || (item.link ? new URL(fixUrl(item.link || item.url || '#')).hostname : 'Unknown'),
          thumbnail: item.thumbnail || item.image,
          position: index + 1,
          category: 'similar'
        };
        categorizedMatches.similar.push(match);
      });
    }

    // Process organic results
    if (data.organic_results) {
      data.organic_results.forEach((item: any, index: number) => {
        const match: DecodoMatch = {
          title: item.title,
          link: fixUrl(item.url || item.link),
          source: item.domain || (item.url ? new URL(fixUrl(item.url)).hostname : 'Unknown'),
          thumbnail: item.url_thumbnail || item.thumbnail || item.image,
          position: index + 1,
          category: 'organic'
        };
        categorizedMatches.organic.push(match);
      });
    }

    // Calculate price analysis
    const priceAnalysis = this.calculatePriceAnalysis(categorizedMatches.shopping);

    // Create flat visual matches array for compatibility
    const visualMatches: VisualMatch[] = [
      ...categorizedMatches.shopping,
      ...categorizedMatches.visual,
      ...categorizedMatches.similar,
      ...categorizedMatches.organic
    ];

    const aiExplanation = this.generateAIExplanation(data, visualMatches);

    return {
      id: Math.random().toString(36).substring(2, 9) + Date.now().toString(36),
      title: `Image Analysis - ${visualMatches.length} matches found`,
      description: aiExplanation,
      value: this.calculateEstimatedValue(visualMatches),
      aiExplanation,
      visualMatches,
      imageUrl: URL.createObjectURL(imageFile),
      apiProvider: ApiProvider.DECODO,
      timestamp: Date.now(),
      categorizedMatches,
      priceAnalysis,
      knowledgeGraph: data.knowledge_graph,
      relatedSearches: data.related_searches
    };
  }

  private extractPriceValue(priceString?: string): number | undefined {
    if (!priceString) return undefined;
    const numericPrice = parseFloat(priceString.replace(/[^0-9.]/g, ''));
    return isNaN(numericPrice) ? undefined : numericPrice;
  }

  private calculatePriceAnalysis(shoppingMatches: DecodoMatch[]) {
    const prices = shoppingMatches
      .map(match => match.extracted_price)
      .filter((price): price is number => price !== undefined && price > 0);

    if (prices.length === 0) return undefined;

    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const avg = prices.reduce((sum, price) => sum + price, 0) / prices.length;

    return {
      min,
      max,
      avg,
      count: prices.length,
      currency: 'USD'
    };
  }
}
